import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { config } from 'config';

type UseScreenshotParams = { variantId: number | null; experimentId: number };

export const useScreenshot = ({ variantId, experimentId }: UseScreenshotParams) => {
  const apiClient = useClient(config.API_URL);
  const queryClient = useQueryClient();

  const { data: screenshots, isLoading } = useQuery({
    queryKey: ['SCREENSHOTS', variantId],
    enabled: !!variantId,
    queryFn: async () => apiClient.get(`/variants/screenshots/${variantId}`),
  });

  // Both write an activity-log entry, and the log is read from the experiment query, so
  // that is refetched too; otherwise the Settings tab shows the entry only on the next focus.
  const deleteScreenshot = async (screenshotId: number) => {
    await apiClient.delete(`/variants/screenshots/${screenshotId}`);
    queryClient.invalidateQueries({ queryKey: ['SCREENSHOTS', variantId] });
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT', experimentId] });
  };

  const updateScreenshot = async (screenshotId: number, comment: string) => {
    await apiClient.put(`/variants/screenshots/${screenshotId}`, { comment });
    queryClient.invalidateQueries({ queryKey: ['SCREENSHOTS', variantId] });
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT', experimentId] });
  };

  return { screenshots, isLoading, deleteScreenshot, updateScreenshot };
};

export type UseScreenshotReturn = ReturnType<typeof useScreenshot>;
